"use client";

import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { KRIPPENDORFF_ALPHA_CALCULATOR_STRINGS } from "@/components/tools/krippendorff-alpha-calculator";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { t } from "@/lib/i18n/interpolate";
import {
  type AlphaMetric,
  type AlphaSummary,
  type BootstrapResult,
  coincidenceMatrix,
  marginals,
  type ReliabilityBand,
  type ReliabilityData,
  reliabilityBand,
} from "@/lib/tool-kit/stats-clinical/krippendorff";
import { type Formatter, fixed } from "@/lib/tool-kit/stats-research/format";

/*
 * The result panel of Krippendorff's alpha calculator: the headline alpha, the four
 * metrics side by side, the bootstrap interval, the coincidence matrix and the working.
 */

type Strings = typeof KRIPPENDORFF_ALPHA_CALCULATOR_STRINGS;

const DASH = "—";
const MAX_MATRIX_CATEGORIES = 12;

const METRIC_KEY: Record<AlphaMetric, keyof Strings> = {
  nominal: "metricNominal",
  ordinal: "metricOrdinal",
  interval: "metricInterval",
  ratio: "metricRatio",
};

const BAND_KEY: Record<ReliabilityBand, keyof Strings> = {
  reliable: "bandReliable",
  tentative: "bandTentative",
  unreliable: "bandUnreliable",
};

function three(fmt: Formatter, value: number): string {
  return Number.isFinite(value) ? fixed(fmt, value, 3) : DASH;
}

function percent(fmt: Formatter, share: number): string {
  return `${fmt(share * 100, { maximumFractionDigits: 1 })}%`;
}

function CoincidenceTable({
  data,
  strings,
  fmt,
}: {
  data: ReliabilityData;
  strings: Strings;
  fmt: Formatter;
}) {
  if (data.labels.length > MAX_MATRIX_CATEGORIES) {
    return (
      <p className="text-muted-foreground text-sm">{strings.matrixTooBig}</p>
    );
  }
  const o = coincidenceMatrix(data.units, data.labels.length);
  const totals = marginals(o);
  const two = (value: number) =>
    fmt(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const grand = totals.reduce((sum, value) => sum + value, 0);
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.matrixHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.matrixNote}</p>
      <DataGrid
        head={["", ...data.labels, strings.total]}
        rows={[
          ...o.map((row, c) => [
            data.labels[c],
            ...row.map(two),
            two(totals[c]),
          ]),
          [strings.total, ...totals.map(two), two(grand)],
        ]}
      />
    </section>
  );
}

function BootstrapPanel({
  bootstrap,
  strings,
  fmt,
  onRun,
}: {
  bootstrap: BootstrapResult | null | undefined;
  strings: Strings;
  fmt: Formatter;
  onRun: () => void;
}) {
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.bootstrapHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.bootstrapNote}</p>
      <button className={BUTTON_SECONDARY} onClick={onRun} type="button">
        {strings.bootstrapRun}
      </button>
      {bootstrap === null ? (
        <p className="text-muted-foreground text-sm">
          {strings.bootstrapFailed}
        </p>
      ) : null}
      {bootstrap ? (
        <div aria-live="polite" className="space-y-1">
          <p className="text-foreground text-sm">
            {t(strings.bootstrapResult, {
              lower: three(fmt, bootstrap.lower),
              upper: three(fmt, bootstrap.upper),
              count: fmt(bootstrap.valid),
            })}
          </p>
          <p className="text-muted-foreground text-sm">
            {t(strings.bootstrapBelow, {
              below667: percent(fmt, bootstrap.below667),
              below800: percent(fmt, bootstrap.below800),
            })}
          </p>
        </div>
      ) : null}
    </section>
  );
}

export function AlphaSummaryPanel({
  data,
  summaries,
  metric,
  bootstrap,
  onBootstrap,
  strings,
  fmt,
}: {
  data: ReliabilityData;
  summaries: AlphaSummary[];
  metric: AlphaMetric;
  bootstrap: BootstrapResult | null | undefined;
  onBootstrap: () => void;
  strings: Strings;
  fmt: Formatter;
}) {
  const selected = summaries.find((entry) => entry.metric === metric);
  if (!selected) {
    return null;
  }
  const { result } = selected;
  const defined = Number.isFinite(result.alpha);
  const metricName = strings[METRIC_KEY[metric]];
  const interval =
    bootstrap && defined
      ? t(strings.reportInterval, {
          lower: three(fmt, bootstrap.lower),
          upper: three(fmt, bootstrap.upper),
        })
      : "";
  const report = defined
    ? t(strings.report, {
        metric: metricName.toLowerCase(),
        alpha: three(fmt, result.alpha),
        interval,
        units: fmt(data.units.length),
        values: fmt(result.n),
      })
    : "";
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {defined ? (
        <>
          <StatGrid>
            <StatCard
              emphasis
              label={t(strings.alphaFor, { metric: metricName.toLowerCase() })}
              value={three(fmt, result.alpha)}
            />
            <StatCard
              label={strings.unitsUsed}
              value={fmt(data.units.length)}
            />
            <StatCard label={strings.pairableValues} value={fmt(result.n)} />
          </StatGrid>
          <p className="text-foreground text-sm leading-relaxed">
            {t(strings.interpretation, {
              alpha: three(fmt, result.alpha),
              band: strings[BAND_KEY[reliabilityBand(result.alpha)]],
            })}{" "}
            <span className="text-muted-foreground">
              {strings.interpretationNote}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              text={report}
            />
            <span className="font-mono text-muted-foreground text-xs">
              {report}
            </span>
          </div>
        </>
      ) : (
        <p className="text-muted-foreground text-sm">
          {strings.undefinedAlpha}
        </p>
      )}
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.allMetrics}</h3>
        <DataGrid
          head={[
            strings.columnMetric,
            strings.columnAlpha,
            strings.columnObserved,
            strings.columnExpected,
          ]}
          highlight={summaries.findIndex((entry) => entry.metric === metric)}
          rows={summaries.map((entry) => [
            strings[METRIC_KEY[entry.metric]],
            three(fmt, entry.result.alpha),
            three(fmt, entry.result.observedDisagreement),
            three(fmt, entry.result.expectedDisagreement),
          ])}
        />
      </section>
      {defined ? (
        <BootstrapPanel
          bootstrap={bootstrap}
          fmt={fmt}
          onRun={onBootstrap}
          strings={strings}
        />
      ) : null}
      <CoincidenceTable data={data} fmt={fmt} strings={strings} />
      {defined ? (
        <div className={`${CARD} space-y-2`}>
          <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
          <WorkingLine>
            {t(strings.workingAlpha, {
              observed: fixed(fmt, result.observedDisagreement, 4),
              expected: fixed(fmt, result.expectedDisagreement, 4),
              alpha: fixed(fmt, result.alpha, 3),
            })}
          </WorkingLine>
          <p className="text-muted-foreground text-sm">
            {t(strings.workingDetail, {
              n: fmt(result.n),
              units: fmt(data.units.length),
            })}
          </p>
        </div>
      ) : null}
    </div>
  );
}
